import React, { useMemo } from "react";
import { Sankey } from "@nivo/sankey";
import { Box, Text, useColorModeValue } from "@chakra-ui/react";
import { PlaidTransaction } from "../../types/transactions";
import _ from "lodash";

export interface SankeyAggregatedData {
  totalIncome: number;
  totalExpenses: number;
  categories: {
    category: string;
    total: number;
    merchants: { merchant: string; total: number }[];
  }[];
}

interface SankeyFlowProps {
  data: PlaidTransaction[];
  width: number;
  height: number;
  /**
   * Pre-aggregated Category -> Merchant sums computed server-side (see
   * PlaidTransactionSyncService.getFlowSankeyData /
   * TransactionsSnapshotService.getSnapshotFlowSankeyData). When provided,
   * this replaces the client-side computation from `data` entirely —
   * `data` is only used as the fallback when this is absent (e.g. the
   * server call hasn't resolved yet, or failed). Large tenants would
   * otherwise need every transaction shipped to the browser just to draw
   * this diagram, which is the exact problem pagination exists to avoid.
   */
  aggregatedData?: SankeyAggregatedData;
}

// 1. Definimos una paleta semántica
const COLORS = {
  budget: "#94A3B8", // Gris azulado neutral
  savings: "#10B981", // Verde esmeralda (Éxito/Ahorro)
  expenses: [
    // Gama de cálidos y fríos para categorías
    "#6366F1",
    "#8B5CF6",
    "#EC4899",
    "#F43F5E",
    "#F59E0B",
    "#3B82F6",
    "#06B6D4",
  ],
};

export const SankeyFlow: React.FC<SankeyFlowProps> = ({
  data,
  width,
  height,
  aggregatedData,
}) => {
  const textColor = useColorModeValue("#4A5568", "#E2E8F0");
  const bgColor = useColorModeValue("white", "#1A202C");
  const nodeThickness = 20;
  const nodeSpacing = 18;

  const theme = useMemo(
    () => ({
      labels: {
        text: {
          fontSize: 13,
          fontWeight: 600,
          fill: textColor, // Texto claro en dark mode, oscuro en light mode
        },
      },
      tooltip: {
        container: {
          background: bgColor,
          color: textColor,
          fontSize: "12px",
          borderRadius: "8px",
          boxShadow: "0 4px 12px rgba(0,0,0,0.5)",
        },
      },
    }),
    [textColor, bgColor],
  );

  const { nodes, links } = useMemo(() => {
    // categoryEntries: [categoryLabel, categoryTotal, [merchantLabel, merchantTotal][]][]
    let categoryEntries: [string, number, [string, number][]][];
    let totalIncome: number;
    let totalExpenses: number;

    if (aggregatedData) {
      if (aggregatedData.categories.length === 0 && aggregatedData.totalIncome === 0) {
        return { nodes: [], links: [] };
      }
      totalIncome = aggregatedData.totalIncome;
      totalExpenses = aggregatedData.totalExpenses;
      categoryEntries = aggregatedData.categories.map((c) => [
        c.category,
        c.total,
        c.merchants.map((m) => [m.merchant, m.total] as [string, number]),
      ]);
    } else if (data && data.length > 0) {
      const expenses = data.filter((t) => t.amount > 0);
      const income = data.filter((t) => t.amount < 0);
      totalExpenses = _.sumBy(expenses, "amount");
      totalIncome = Math.abs(_.sumBy(income, "amount"));

      const expensesByCategory = _.groupBy(
        expenses,
        (t) => t.personalFinanceCategoryPrimary || "Other",
      );
      categoryEntries = Object.entries(expensesByCategory).map(([category, catTrans]) => {
        const expensesByMerchant = _.groupBy(catTrans, (t) => t.merchantName || t.name || "Unknown");
        return [
          category,
          _.sumBy(catTrans, "amount"),
          Object.entries(expensesByMerchant).map(
            ([merchant, merchTrans]) => [merchant, _.sumBy(merchTrans, "amount")] as [string, number],
          ),
        ];
      });
    } else {
      return { nodes: [], links: [] };
    }

    const nodeMap = new Map<string, { id: string; color: string }>();
    const linksList: any[] = [];

    // Nodo Raíz
    const budgetId = "Total Budget";
    nodeMap.set(budgetId, { id: budgetId, color: COLORS.budget });

    const truncate = (str: string, len: number = 25) => _.truncate(str, { length: len });

    categoryEntries.forEach(([category, catTotal, merchantEntries], index) => {
      const catNodeId = truncate(category);

      // Asignamos color de la paleta de gastos
      nodeMap.set(catNodeId, {
        id: catNodeId,
        color: COLORS.expenses[index % COLORS.expenses.length],
      });

      linksList.push({
        source: budgetId,
        target: catNodeId,
        value: catTotal,
      });

      merchantEntries.forEach(([merchant, merchTotal]) => {
        const merchNodeId = truncate(merchant);
        const finalMerchId = merchNodeId === catNodeId ? `${merchNodeId} ` : merchNodeId;

        // Los comercios heredan una versión más suave del color de su categoría
        nodeMap.set(finalMerchId, {
          id: finalMerchId,
          color: nodeMap.get(catNodeId)?.color || COLORS.expenses[0],
        });

        linksList.push({
          source: catNodeId,
          target: finalMerchId,
          value: merchTotal,
        });
      });
    });

    // Nodo de Ahorro
    if (totalIncome > totalExpenses) {
      const savingsId = "Savings / Surplus";
      nodeMap.set(savingsId, { id: savingsId, color: COLORS.savings });
      linksList.push({
        source: budgetId,
        target: savingsId,
        value: totalIncome - totalExpenses,
      });
    }

    return {
      nodes: Array.from(nodeMap.values()),
      links: linksList,
    };
  }, [data, aggregatedData]);

  if (nodes.length === 0) return null;

  return (
    <div style={{ width, height, position: "relative" }}>
      <Sankey
        width={width}
        height={height}
        data={{ nodes, links }}
        margin={{ top: 20, right: 180, bottom: 20, left: 120 }}
        align="justify"
        // @ts-ignore
        colors={(node) => node.color}
        animate={false}
        nodeOpacity={1}
        nodeThickness={nodeThickness}
        nodeSpacing={nodeSpacing}
        nodeBorderWidth={0}
        enableLinkGradient={true}
        linkOpacity={0.25}
        linkHoverOpacity={0.6}
        linkContract={2}
        labelPosition="outside"
        labelOrientation="horizontal"
        labelPadding={16}
        labelTextColor={{ from: "color", modifiers: [["darker", 1.5]] }}
        theme={theme}
      />
    </div>
  );
};
